import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Autocomplete,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormGroup,
  Checkbox
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Clear,
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Language,
  AccountBalance,
  CreditCard,
  Description,
  Group,
  Settings,
  Security,
  Notifications,
  ExpandMore,
  Add,
  Delete,
  Edit,
  Upload,
  CloudUpload,
  Palette,
  Public,
  Lock,
  Schedule,
  Assessment,
  Storage
} from '@mui/icons-material';
import { ErrorOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import companyService from '../services/companyService';

interface Department {
  id: string;
  name: string;
  description: string;
  manager: string;
  employeeCount: number;
}

interface BrandingSettings {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  faviconUrl: string;
  companySlogan: string;
  customCss: string;
}

interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    expirationDays: number;
  };
  twoFactorAuth: boolean;
  sessionTimeout: number;
  ipWhitelist: string[];
  auditLogging: boolean;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  dailyReports: boolean;
  weeklyReports: boolean;
  monthlyReports: boolean;
  systemAlerts: boolean;
  maintenanceAlerts: boolean;
}

interface CompanyFormData {
  // Basic Information
  companyName: string;
  legalName: string;
  companyType: string;
  industry: string;
  foundedYear: string;
  employeeCount: string;
  website: string;
  description: string;
  
  // Contact Information
  primaryEmail: string;
  primaryPhone: string;
  faxNumber: string;
  
  // Address Information
  headquarters: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  
  // Business Details
  taxId: string;
  businessLicense: string;
  insuranceProvider: string;
  insurancePolicyNumber: string;
  bondingCompany: string;
  bondAmount: string;
  
  // Departments
  departments: Department[];
  
  // Settings
  timezone: string;
  currency: string;
  fiscalYearStart: string;
  workingDays: string[];
  workingHours: {
    start: string;
    end: string;
  };
  
  // Branding
  branding: BrandingSettings;
  
  // Security
  security: SecuritySettings;
  
  // Notifications
  notifications: NotificationSettings;
  
  // Features
  enabledFeatures: string[];
  
  // Compliance
  certifications: string[];
  complianceStandards: string[];
}

const CompanyCreate: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<CompanyFormData>({
    // Basic Information
    companyName: '',
    legalName: '',
    companyType: 'LLC',
    industry: 'General Construction',
    foundedYear: '',
    employeeCount: '1-10',
    website: '',
    description: '',
    
    // Contact Information
    primaryEmail: '',
    primaryPhone: '',
    faxNumber: '',
    
    // Address Information
    headquarters: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    
    // Business Details
    taxId: '',
    businessLicense: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    bondingCompany: '',
    bondAmount: '',
    
    // Departments
    departments: [],
    
    // Settings
    timezone: 'America/New_York',
    currency: 'USD',
    fiscalYearStart: 'January',
    workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    workingHours: {
      start: '08:00',
      end: '17:00'
    },
    
    // Branding
    branding: {
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      logoUrl: '',
      faviconUrl: '',
      companySlogan: '',
      customCss: ''
    },
    
    // Security
    security: {
      passwordPolicy: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: false,
        expirationDays: 90
      },
      twoFactorAuth: false,
      sessionTimeout: 30,
      ipWhitelist: [],
      auditLogging: true
    },
    
    // Notifications
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      dailyReports: false,
      weeklyReports: true,
      monthlyReports: true,
      systemAlerts: true,
      maintenanceAlerts: true
    },
    
    // Features
    enabledFeatures: ['project-management', 'financial-tracking', 'document-management'],
    
    // Compliance
    certifications: [],
    complianceStandards: []
  });

  const [errors, setErrors] = useState<Partial<CompanyFormData>>({});
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const companyTypes = [
    'LLC',
    'Corporation',
    'Partnership',
    'Sole Proprietorship',
    'S-Corporation',
    'Non-Profit',
    'Government Agency'
  ];

  const industries = [
    'General Construction',
    'Residential Construction',
    'Commercial Construction',
    'Industrial Construction',
    'Civil Engineering',
    'Electrical Contracting',
    'Plumbing Contracting',
    'HVAC Contracting',
    'Roofing',
    'Concrete Work',
    'Landscaping',
    'Demolition',
    'Specialty Contracting'
  ];

  const employeeCounts = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu'
  ];

  const currencies = [
    'USD',
    'CAD',
    'EUR',
    'GBP',
    'AUD',
    'JPY'
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const availableFeatures = [
    'project-management',
    'financial-tracking',
    'document-management',
    'time-tracking',
    'inventory-management',
    'client-portal',
    'mobile-app',
    'reporting-analytics',
    'quality-control',
    'safety-management',
    'equipment-tracking',
    'subcontractor-management'
  ];

  const certificationOptions = [
    'ISO 9001',
    'ISO 14001',
    'OHSAS 18001',
    'LEED Certification',
    'Green Building Certification',
    'Safety Certification',
    'Quality Management Certification'
  ];

  const complianceOptions = [
    'OSHA Standards',
    'EPA Regulations',
    'Local Building Codes',
    'State Licensing Requirements',
    'Federal Contracting Standards',
    'Industry Safety Standards',
    'Environmental Compliance'
  ];

  const handleInputChange = (field: keyof CompanyFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleHeadquartersChange = (field: keyof CompanyFormData['headquarters']) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      headquarters: { ...prev.headquarters, [field]: value }
    }));

    // Clear nested field error when user starts typing
    const hqErrors = (errors.headquarters as any) || {};
    if (hqErrors && hqErrors[field]) {
      const newHqErrors = { ...hqErrors, [field]: undefined };
      setErrors(prev => ({ ...prev, headquarters: newHqErrors as any }));
    }
  };

  const handleWorkingHoursChange = (field: 'start' | 'end') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      workingHours: { ...prev.workingHours, [field]: value }
    }));
  };

  const handleBrandingChange = (field: keyof BrandingSettings) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      branding: { ...prev.branding, [field]: value }
    }));
  };

  const handleSecurityChange = (field: string, value: any) => {
    if (field.startsWith('passwordPolicy.')) {
      const policyField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        security: {
          ...prev.security,
          passwordPolicy: {
            ...prev.security.passwordPolicy,
            [policyField]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        security: { ...prev.security, [field]: value }
      }));
    }
  };

  const handleNotificationChange = (field: keyof NotificationSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setFormData(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [field]: checked }
    }));
  };

  const addDepartment = () => {
    const newDepartment: Department = {
      id: Date.now().toString(),
      name: '',
      description: '',
      manager: '',
      employeeCount: 0
    };
    setFormData(prev => ({
      ...prev,
      departments: [...prev.departments, newDepartment]
    }));
  };

  const updateDepartment = (id: string, field: keyof Department, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.map(dept =>
        dept.id === id ? { ...dept, [field]: value } : dept
      )
    }));
  };

  const removeDepartment = (id: string) => {
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.filter(dept => dept.id !== id)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CompanyFormData> = {};

    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.legalName.trim()) newErrors.legalName = 'Legal name is required';
    if (!formData.primaryEmail.trim()) newErrors.primaryEmail = 'Primary email is required';
    if (!formData.primaryPhone.trim()) newErrors.primaryPhone = 'Primary phone is required';
    if (!formData.headquarters.address.trim()) newErrors.headquarters = { address: 'Address is required' } as any;
    if (!formData.headquarters.city.trim()) newErrors.headquarters = { ...newErrors.headquarters as any, city: 'City is required' };
    if (!formData.headquarters.state.trim()) newErrors.headquarters = { ...newErrors.headquarters as any, state: 'State is required' };
    if (!formData.headquarters.zipCode.trim()) newErrors.headquarters = { ...newErrors.headquarters as any, zipCode: 'ZIP code is required' };

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.primaryEmail && !emailRegex.test(formData.primaryEmail)) {
      newErrors.primaryEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;

    // Auto-switch to the tab containing the first error to guide the user
    if (!isValid) {
      const hasBasicErrors = !!(newErrors.companyName || newErrors.legalName);
      const hasContactErrors = !!(newErrors.primaryEmail || newErrors.primaryPhone || newErrors.headquarters);
      if (hasBasicErrors) setActiveTab(0);
      else if (hasContactErrors) setActiveTab(1);
    }

    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');

    try {
      await companyService.createCompany(formData);
      setSubmitStatus('success');
      setTimeout(() => {
        navigate('/company-management');
      }, 800);
    } catch (error) {
      console.error('Error creating company:', error);
      setSubmitStatus('error');
    }
  };

  const handleReset = () => {
    // Reset to initial state
    setFormData({
      companyName: '',
      legalName: '',
      companyType: 'LLC',
      industry: 'General Construction',
      foundedYear: '',
      employeeCount: '1-10',
      website: '',
      description: '',
      primaryEmail: '',
      primaryPhone: '',
      faxNumber: '',
      headquarters: {
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
      },
      taxId: '',
      businessLicense: '',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      bondingCompany: '',
      bondAmount: '',
      departments: [],
      timezone: 'America/New_York',
      currency: 'USD',
      fiscalYearStart: 'January',
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      workingHours: {
        start: '08:00',
        end: '17:00'
      },
      branding: {
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        logoUrl: '',
        faviconUrl: '',
        companySlogan: '',
        customCss: ''
      },
      security: {
        passwordPolicy: {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false,
          expirationDays: 90
        },
        twoFactorAuth: false,
        sessionTimeout: 30,
        ipWhitelist: [],
        auditLogging: true
      },
      notifications: {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        dailyReports: false,
        weeklyReports: true,
        monthlyReports: true,
        systemAlerts: true,
        maintenanceAlerts: true
      },
      enabledFeatures: ['project-management', 'financial-tracking', 'document-management'],
      certifications: [],
      complianceStandards: []
    });
    setErrors({});
    setSubmitStatus('idle');
    setActiveTab(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'loading': return 'info';
      default: return 'default';
    }
  };

  const renderBasicInfo = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Company Names */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          fullWidth
          label="Company Name"
          value={formData.companyName}
          onChange={handleInputChange('companyName')}
          error={!!errors.companyName}
          helperText={errors.companyName}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Business color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Legal Name"
          value={formData.legalName}
          onChange={handleInputChange('legalName')}
          error={!!errors.legalName}
          helperText={errors.legalName || 'Official legal name for contracts and documents'}
          required
        />
      </Box>

      {/* Company Type & Industry */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Company Type</InputLabel>
          <Select
            value={formData.companyType}
            onChange={(e) => setFormData(prev => ({ ...prev, companyType: e.target.value }))}
            label="Company Type"
          >
            {companyTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Industry</InputLabel>
          <Select
            value={formData.industry}
            onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
            label="Industry"
          >
            {industries.map((industry) => (
              <MenuItem key={industry} value={industry}>
                {industry}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Founded Year & Employee Count */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          fullWidth
          label="Founded Year"
          value={formData.foundedYear}
          onChange={handleInputChange('foundedYear')}
          type="number"
          inputProps={{ min: 1800, max: new Date().getFullYear() }}
        />

        <FormControl fullWidth>
          <InputLabel>Employee Count</InputLabel>
          <Select
            value={formData.employeeCount}
            onChange={(e) => setFormData(prev => ({ ...prev, employeeCount: e.target.value }))}
            label="Employee Count"
          >
            {employeeCounts.map((count) => (
              <MenuItem key={count} value={count}>
                {count}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Website */}
      <TextField
        fullWidth
        label="Website"
        value={formData.website}
        onChange={handleInputChange('website')}
        placeholder="https://www.example.com"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Language color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Description */}
      <TextField
        fullWidth
        label="Company Description"
        value={formData.description}
        onChange={handleInputChange('description')}
        multiline
        rows={4}
        placeholder="Brief description of your company's services and specialties"
      />
    </Box>
  );

  const renderContactInfo = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Contact Information */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Contact Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Primary Email"
              value={formData.primaryEmail}
              onChange={handleInputChange('primaryEmail')}
              error={!!errors.primaryEmail}
              helperText={errors.primaryEmail}
              required
              type="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Primary Phone"
              value={formData.primaryPhone}
              onChange={handleInputChange('primaryPhone')}
              error={!!errors.primaryPhone}
              helperText={errors.primaryPhone}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Fax Number"
            value={formData.faxNumber}
            onChange={handleInputChange('faxNumber')}
            placeholder="Optional"
          />
        </Box>
      </Box>

      {/* Headquarters Address */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Headquarters Address
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            fullWidth
            label="Street Address"
            value={formData.headquarters.address}
            onChange={handleHeadquartersChange('address')}
            error={!!(errors.headquarters as any)?.address}
            helperText={(errors.headquarters as any)?.address}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.headquarters.city}
              onChange={handleHeadquartersChange('city')}
              error={!!(errors.headquarters as any)?.city}
              helperText={(errors.headquarters as any)?.city}
              required
            />

            <TextField
              fullWidth
              label="State"
              value={formData.headquarters.state}
              onChange={handleHeadquartersChange('state')}
              error={!!(errors.headquarters as any)?.state}
              helperText={(errors.headquarters as any)?.state}
              required
            />

            <TextField
              fullWidth
              label="ZIP Code"
              value={formData.headquarters.zipCode}
              onChange={handleHeadquartersChange('zipCode')}
              error={!!(errors.headquarters as any)?.zipCode}
              helperText={(errors.headquarters as any)?.zipCode}
              required
            />

            <TextField
              fullWidth
              label="Country"
              value={formData.headquarters.country}
              onChange={handleHeadquartersChange('country')}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderBusinessDetails = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Legal & Licensing */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Legal & Licensing Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Tax ID / EIN"
              value={formData.taxId}
              onChange={handleInputChange('taxId')}
              placeholder="Federal Tax ID or EIN"
            />

            <TextField
              fullWidth
              label="Business License Number"
              value={formData.businessLicense}
              onChange={handleInputChange('businessLicense')}
              placeholder="State business license number"
            />
          </Box>
        </Box>
      </Box>

      {/* Insurance & Bonding */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Insurance & Bonding
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Insurance Provider"
              value={formData.insuranceProvider}
              onChange={handleInputChange('insuranceProvider')}
              placeholder="General liability insurance provider"
            />

            <TextField
              fullWidth
              label="Insurance Policy Number"
              value={formData.insurancePolicyNumber}
              onChange={handleInputChange('insurancePolicyNumber')}
              placeholder="Policy number"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Bonding Company"
              value={formData.bondingCompany}
              onChange={handleInputChange('bondingCompany')}
              placeholder="Surety bond provider"
            />

            <TextField
              fullWidth
              label="Bond Amount"
              value={formData.bondAmount}
              onChange={handleInputChange('bondAmount')}
              placeholder="e.g., $500,000"
            />
          </Box>
        </Box>
      </Box>

      {/* Certifications & Compliance */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Certifications & Compliance
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Autocomplete
            multiple
            options={certificationOptions}
            value={formData.certifications}
            onChange={(_, newValue) => {
              setFormData(prev => ({ ...prev, certifications: newValue }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Certifications"
                placeholder="Select company certifications"
              />
            )}
          />

          <Autocomplete
            multiple
            options={complianceOptions}
            value={formData.complianceStandards}
            onChange={(_, newValue) => {
              setFormData(prev => ({ ...prev, complianceStandards: newValue }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={index}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Compliance Standards"
                placeholder="Select compliance standards"
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderDepartments = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" color="primary">
          Company Departments
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={addDepartment}
        >
          Add Department
        </Button>
      </Box>
      <Divider />
      
      {formData.departments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No departments added yet. Click "Add Department" to create organizational structure.
        </Typography>
      ) : (
        <List>
          {formData.departments.map((department) => (
            <ListItem key={department.id} divider>
              <ListItemAvatar>
                <Avatar>
                  <Group />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Department Name"
                      value={department.name}
                      onChange={(e) => updateDepartment(department.id, 'name', e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Manager"
                      value={department.manager}
                      onChange={(e) => updateDepartment(department.id, 'manager', e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Employee Count"
                      type="number"
                      value={department.employeeCount}
                      onChange={(e) => updateDepartment(department.id, 'employeeCount', parseInt(e.target.value) || 0)}
                    />
                  </Box>
                }
                secondary={
                  <TextField
                    size="small"
                    label="Description"
                    value={department.description}
                    onChange={(e) => updateDepartment(department.id, 'description', e.target.value)}
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                }
              />
              <IconButton
                edge="end"
                color="error"
                onClick={() => removeDepartment(department.id)}
              >
                <Delete />
              </IconButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  const renderSettings = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* General Settings */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          General Settings
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                label="Timezone"
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                label="Currency"
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Fiscal Year Start</InputLabel>
              <Select
                value={formData.fiscalYearStart}
                onChange={(e) => setFormData(prev => ({ ...prev, fiscalYearStart: e.target.value }))}
                label="Fiscal Year Start"
              >
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Working Schedule */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Working Schedule
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Working Days
            </Typography>
            <FormGroup row>
              {weekDays.map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={formData.workingDays.includes(day)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            workingDays: [...prev.workingDays, day]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            workingDays: prev.workingDays.filter(d => d !== day)
                          }));
                        }
                      }}
                    />
                  }
                  label={day.substring(0, 3)}
                />
              ))}
            </FormGroup>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              label="Start Time"
              type="time"
              value={formData.workingHours.start}
              onChange={handleWorkingHoursChange('start')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="End Time"
              type="time"
              value={formData.workingHours.end}
              onChange={handleWorkingHoursChange('end')}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </Box>

      {/* Enabled Features */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Enabled Features
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Autocomplete
          multiple
          options={availableFeatures}
          value={formData.enabledFeatures}
          onChange={(_, newValue) => {
            setFormData(prev => ({ ...prev, enabledFeatures: newValue }));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="System Features"
              placeholder="Select features to enable for your company"
            />
          )}
        />
      </Box>
    </Box>
  );

  const renderBranding = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Colors */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Brand Colors
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            fullWidth
            label="Primary Color"
            type="color"
            value={formData.branding.primaryColor}
            onChange={handleBrandingChange('primaryColor')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Palette color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Secondary Color"
            type="color"
            value={formData.branding.secondaryColor}
            onChange={handleBrandingChange('secondaryColor')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Palette color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Logo & Assets */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Logo & Assets
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            fullWidth
            label="Logo URL"
            value={formData.branding.logoUrl}
            onChange={handleBrandingChange('logoUrl')}
            placeholder="https://example.com/logo.png"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CloudUpload color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Favicon URL"
            value={formData.branding.faviconUrl}
            onChange={handleBrandingChange('faviconUrl')}
            placeholder="https://example.com/favicon.ico"
          />

          <TextField
            fullWidth
            label="Company Slogan"
            value={formData.branding.companySlogan}
            onChange={handleBrandingChange('companySlogan')}
            placeholder="Your company's tagline or slogan"
          />
        </Box>
      </Box>

      {/* Custom CSS */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Custom Styling
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <TextField
          fullWidth
          label="Custom CSS"
          value={formData.branding.customCss}
          onChange={handleBrandingChange('customCss')}
          multiline
          rows={6}
          placeholder="/* Add custom CSS styles here */"
          helperText="Advanced: Custom CSS to override default styling"
        />
      </Box>
    </Box>
  );

  const renderSecurity = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Password Policy */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Password Policy
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Minimum Length: {formData.security.passwordPolicy.minLength} characters
            </Typography>
            <Slider
              value={formData.security.passwordPolicy.minLength}
              onChange={(_, value) => handleSecurityChange('passwordPolicy.minLength', value)}
              min={6}
              max={20}
              marks
              valueLabelDisplay="auto"
            />
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.security.passwordPolicy.requireUppercase}
                  onChange={(e) => handleSecurityChange('passwordPolicy.requireUppercase', e.target.checked)}
                />
              }
              label="Require uppercase letters"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.security.passwordPolicy.requireLowercase}
                  onChange={(e) => handleSecurityChange('passwordPolicy.requireLowercase', e.target.checked)}
                />
              }
              label="Require lowercase letters"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.security.passwordPolicy.requireNumbers}
                  onChange={(e) => handleSecurityChange('passwordPolicy.requireNumbers', e.target.checked)}
                />
              }
              label="Require numbers"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.security.passwordPolicy.requireSpecialChars}
                  onChange={(e) => handleSecurityChange('passwordPolicy.requireSpecialChars', e.target.checked)}
                />
              }
              label="Require special characters"
            />
          </FormGroup>

          <TextField
            fullWidth
            label="Password Expiration (Days)"
            type="number"
            value={formData.security.passwordPolicy.expirationDays}
            onChange={(e) => handleSecurityChange('passwordPolicy.expirationDays', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: 365 }}
            helperText="Set to 0 for no expiration"
          />
        </Box>
      </Box>

      {/* Security Settings */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Security Settings
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'grid', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.security.twoFactorAuth}
                onChange={(e) => handleSecurityChange('twoFactorAuth', e.target.checked)}
              />
            }
            label="Enable Two-Factor Authentication"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.security.auditLogging}
                onChange={(e) => handleSecurityChange('auditLogging', e.target.checked)}
              />
            }
            label="Enable Audit Logging"
          />

          <TextField
            fullWidth
            label="Session Timeout (Minutes)"
            type="number"
            value={formData.security.sessionTimeout}
            onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value) || 30)}
            inputProps={{ min: 5, max: 480 }}
            helperText="Automatic logout after inactivity"
          />
        </Box>
      </Box>
    </Box>
  );

  const renderNotifications = () => (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {/* Notification Channels */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Notification Channels
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.emailNotifications}
                onChange={handleNotificationChange('emailNotifications')}
              />
            }
            label="Email Notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.smsNotifications}
                onChange={handleNotificationChange('smsNotifications')}
              />
            }
            label="SMS Notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.pushNotifications}
                onChange={handleNotificationChange('pushNotifications')}
              />
            }
            label="Push Notifications"
          />
        </FormGroup>
      </Box>

      {/* Report Schedules */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          Automated Reports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.dailyReports}
                onChange={handleNotificationChange('dailyReports')}
              />
            }
            label="Daily Reports"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.weeklyReports}
                onChange={handleNotificationChange('weeklyReports')}
              />
            }
            label="Weekly Reports"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.monthlyReports}
                onChange={handleNotificationChange('monthlyReports')}
              />
            }
            label="Monthly Reports"
          />
        </FormGroup>
      </Box>

      {/* System Alerts */}
      <Box>
        <Typography variant="h6" gutterBottom color="primary">
          System Alerts
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.systemAlerts}
                onChange={handleNotificationChange('systemAlerts')}
              />
            }
            label="System Alerts"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.maintenanceAlerts}
                onChange={handleNotificationChange('maintenanceAlerts')}
              />
            }
            label="Maintenance Alerts"
          />
        </FormGroup>
      </Box>
    </Box>
  );

  const tabContent = [
    { label: 'Basic Info', content: renderBasicInfo() },
    { label: 'Contact & Address', content: renderContactInfo() },
    { label: 'Business Details', content: renderBusinessDetails() },
    { label: 'Departments', content: renderDepartments() },
    { label: 'Settings', content: renderSettings() },
    { label: 'Branding', content: renderBranding() },
    { label: 'Security', content: renderSecurity() },
    { label: 'Notifications', content: renderNotifications() }
  ];

  // Build a concise error summary to show which fields need attention
  const getErrorSummary = () => {
    const summary: string[] = [];
    if (errors.companyName) summary.push('Company Name');
    if (errors.legalName) summary.push('Legal Name');
    if (errors.primaryEmail) summary.push('Primary Email');
    if (errors.primaryPhone) summary.push('Primary Phone');
    const hqErrors = (errors.headquarters as any) || {};
    if (hqErrors.address) summary.push('Street Address');
    if (hqErrors.city) summary.push('City');
    if (hqErrors.state) summary.push('State');
    if (hqErrors.zipCode) summary.push('ZIP Code');
    return summary;
  };

  const tabHasError = (index: number) => {
    switch (index) {
      case 0:
        return !!(errors.companyName || errors.legalName);
      case 1:
        return !!(errors.primaryEmail || errors.primaryPhone || errors.headquarters);
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/company-management')} 
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Create Company Profile
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Set up your company information, departments, settings, and preferences
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Status Alert */}
      {submitStatus !== 'idle' && (
        <Alert 
          severity={getStatusColor(submitStatus) as any}
          sx={{ mb: 3 }}
          onClose={() => setSubmitStatus('idle')}
        >
          {submitStatus === 'loading' && 'Creating company profile...'}
          {submitStatus === 'success' && 'Company created successfully! Redirecting...'}
          {submitStatus === 'error' && (
            <>Please fix the following fields: {getErrorSummary().join(', ') || 'None'}.</>
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card elevation={2}>
          <CardContent>
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3 }}
            >
              {tabContent.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tabHasError(index) ? <ErrorOutline color="error" fontSize="small" /> : undefined}
                  iconPosition="end"
                />
              ))}
            </Tabs>

            <Divider sx={{ mb: 3 }} />

            {/* Tab Content */}
            <Box sx={{ minHeight: 500 }}>
              {tabContent[activeTab].content}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<Clear />}
                disabled={submitStatus === 'loading'}
              >
                Reset Form
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={submitStatus === 'loading'}
                size="large"
              >
                {submitStatus === 'loading' ? 'Creating...' : 'Create Company'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default CompanyCreate;